// ╔══════════════════════════════════════════════════════════════╗
// ║  API client — axios instance with auth + refresh handling     ║
// ╚══════════════════════════════════════════════════════════════╝

import axios from 'axios';

const BASE_URL = import.meta.env.VITE_API_URL || '/api';

export const api = axios.create({
  baseURL: BASE_URL,
  withCredentials: true, // for refresh-token cookie
  timeout: 30_000,
});

// ─── Access token state (in-memory only — not persisted) ───
let accessToken = null;
let refreshPromise = null;
const listeners = new Set();

export function setAccessToken(token) {
  accessToken = token;
  if (token) localStorage.setItem('hindam_token_present', '1');
  else localStorage.removeItem('hindam_token_present');
  for (const l of listeners) l(token);
}

export function getAccessToken() {
  return accessToken;
}

export function onTokenChange(cb) {
  listeners.add(cb);
  return () => listeners.delete(cb);
}

// ─── Request interceptor: attach token ───
api.interceptors.request.use((cfg) => {
  if (accessToken) {
    cfg.headers.Authorization = `Bearer ${accessToken}`;
  }
  return cfg;
});

// ─── Response interceptor: auto-refresh on 401 ───
api.interceptors.response.use(
  (res) => res,
  async (err) => {
    const original = err.config;
    if (
      err.response?.status === 401 &&
      !original._retried &&
      !original.url?.endsWith('/auth/refresh') &&
      !original.url?.endsWith('/auth/login')
    ) {
      original._retried = true;
      try {
        if (!refreshPromise) {
          refreshPromise = api
            .post('/auth/refresh')
            .then((r) => r.data.accessToken)
            .finally(() => {
              refreshPromise = null;
            });
        }
        const newToken = await refreshPromise;
        setAccessToken(newToken);
        original.headers.Authorization = `Bearer ${newToken}`;
        return api.request(original);
      } catch (refreshErr) {
        setAccessToken(null);
        // Notify auth context
        window.dispatchEvent(new CustomEvent('hindam:logged-out'));
        return Promise.reject(refreshErr);
      }
    }
    return Promise.reject(err);
  },
);

// ─── Helper to extract Arabic error message ───
export function getErrorMessage(err) {
  if (err?.response?.data?.error?.message) return err.response.data.error.message;
  if (err?.message) return err.message;
  return 'حدث خطأ غير متوقع.';
}

// ─── Authenticated file download (CSV/PDF) ───
// Uses axios (which includes the Bearer token) to fetch the file as a blob,
// then triggers a browser download. Replaces window.open() which doesn't
// send the Authorization header.
export async function downloadFile(path, filename) {
  try {
    const res = await api.get(path, { responseType: 'blob' });
    const blob = new Blob([res.data]);
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename || 'download';
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(url);
  } catch (err) {
    console.error('Download failed:', err);
    throw err;
  }
}

// ─── Open file in new tab (for PDFs) ───
export async function openFileInNewTab(path) {
  const res = await api.get(path, { responseType: 'blob' });
  const blob = new Blob([res.data], { type: res.data.type || 'application/pdf' });
  const url = window.URL.createObjectURL(blob);
  window.open(url, '_blank');
  // revoke after 60s to allow viewing
  setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
}
